import { useEffect, useState } from 'react'
import { motion, AnimatePresence, Reorder } from 'framer-motion'
import LeaderboardRow from '../components/LeaderboardRow.jsx'
import LivePulseDot from '../components/LivePulseDot.jsx'
import CountdownTimer from '../components/CountdownTimer.jsx'
import ActivityTicker from '../components/ActivityTicker.jsx'
import { useRealtimeLeaderboard } from '../hooks/useRealtimeLeaderboard.js'

const SLIDES = ['leaderboard', 'recent', 'progress', 'topweek']

export default function TVMode() {
  const { rows } = useRealtimeLeaderboard()
  const [slide, setSlide] = useState(0)

  useEffect(() => {
    const id = setInterval(() => setSlide(s => (s + 1) % SLIDES.length), 14000)
    return () => clearInterval(id)
  }, [])

  return (
    <div className="min-h-screen tv-bg p-12 flex flex-col">
      <header className="flex items-center justify-between mb-10">
        <div className="flex items-center gap-4">
          <img src="/climatise-logo.svg" alt="Climatise" className="h-10 brightness-0 invert" />
          <div className="font-display text-3xl text-brand-blue/90">LinkedIn league</div>
        </div>
        <div className="flex items-center gap-6">
          <LivePulseDot label="Live" dark />
          <CountdownTimer dark />
        </div>
      </header>

      <div className="flex-1 max-w-5xl mx-auto w-full">
        <AnimatePresence mode="wait">
          {SLIDES[slide] === 'leaderboard' && (
            <motion.section
              key="lb"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
            >
              <h2 className="text-4xl font-bold mb-6">Leaderboard</h2>
              <Reorder.Group axis="y" values={rows} onReorder={() => {}} className="space-y-4">
                {rows.slice(0, 6).map(r => (
                  <LeaderboardRow key={r.participant.id} row={r} dark />
                ))}
              </Reorder.Group>
            </motion.section>
          )}

          {SLIDES[slide] === 'recent' && (
            <SlideShell key="recent" title="Recent posts">
              <p className="text-white/60 text-xl">Last 10 minutes scrolling below.</p>
            </SlideShell>
          )}

          {SLIDES[slide] === 'progress' && (
            <SlideShell key="progress" title="Quarterly progress">
              <QuarterProgress rows={rows} />
            </SlideShell>
          )}

          {SLIDES[slide] === 'topweek' && (
            <SlideShell key="tw" title="Top of the week">
              <p className="text-white/60 text-xl">Top single posts from the past 7 days.</p>
            </SlideShell>
          )}
        </AnimatePresence>
      </div>

      <footer className="text-center text-white/30 text-sm mt-8">
        linkedin.climatise.com · auto-cycling every 14s
      </footer>

      <ActivityTicker dark />
    </div>
  )
}

function SlideShell({ title, children }) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.4 }}
    >
      <h2 className="text-4xl font-bold mb-6">{title}</h2>
      <div className="bg-white/5 border border-white/10 rounded-2xl p-10">
        {children}
      </div>
    </motion.section>
  )
}

function QuarterProgress({ rows }) {
  const totalPosts = rows.reduce((s, r) => s + (r.byDay?.length || 0), 0)
  const totalPoints = rows.reduce((s, r) => s + r.points, 0)
  return (
    <div className="grid grid-cols-3 gap-8">
      <Stat label="Total points" value={totalPoints.toLocaleString()} />
      <Stat label="Active people" value={rows.filter(r => r.points > 0).length} />
      <Stat label="Days logged" value={totalPosts} />
    </div>
  )
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-white/50 mb-2">{label}</div>
      <div className="text-5xl font-bold">{value}</div>
    </div>
  )
}
